/* Trappbutiken – produktsida (Wubs)
 * <wubs-variant-picker> löser variantval på gles matris som en KASKAD: första axeln visar alla
 * värden, varje senare axel bara de som går ihop med valen ovanför (resten döljs).
 * Uppdaterar pris, bild, SKU, ATC-variant och URL.
 */
class WubsVariantPicker extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.dataset.variants);
    } catch (e) {
      this.variants = [];
    }
    // variant→media-karta (Shopify serialiserar inte featured_media i variant-JSON)
    try {
      this.variantMedia = JSON.parse(this.dataset.variantMedia || '{}');
    } catch (e) {
      this.variantMedia = {};
    }
    /* Varianter som lämnar över köprutan till priskonfiguratorn (`custom.matt_modell`).
       Listan renderas av wubs-variant-options.liquid, inuti pickern, så att produktsidan och
       quick add-panelen får den ur samma källa. Saknas den är mängden tom – alla axlar visas. */
    this.mattVarianter = new Set();
    try {
      const rad = this.querySelector('[data-matt-varianter]');
      if (rad) JSON.parse(rad.textContent).forEach((id) => this.mattVarianter.add(Number(id)));
    } catch (e) {}
    this.productUrl = this.dataset.productUrl || '';
    this.pdp = this.closest('[data-wubs-pdp]');

    // Swatch-bilder: värde → första variantbild med det värdet
    this.paintSwatches();

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-option-input]')) this.onSelect(e.target);
    });

    // Liquid har redan dolt det som inte går ihop med startvarianten (wubs-variant-options).
    // Samma regel körs här ändå – är de två någon gång oense ska skriptet ha sista ordet
    // direkt vid laddning, inte först vid kundens första klick.
    this.refreshAvailability();
    // Init: sätt pris/ATC/URL men behåll produktens huvudbild (byt bild först vid aktivt variantval).
    this.update(this.currentVariant(), { skipMedia: true });
  }

  selectedValues() {
    return Array.from(this.querySelectorAll('[data-option-input]:checked')).map((i) => i.value);
  }

  /* Valet per axel, indexerat på option-POSITION (0-baserat) – inte på DOM-ordning.
     En överhoppad axel (Tillägg/Del/Title) har ingen fieldset och blir undefined, vilket
     i finns() betyder "ingen begränsning". */
  valPerPosition() {
    const val = [];
    this.querySelectorAll('[data-option-position]').forEach((fs) => {
      const c = fs.querySelector('[data-option-input]:checked');
      if (c) val[Number(fs.dataset.optionPosition) - 1] = c.value;
    });
    return val;
  }

  /* Finns en variant med `varde` på axel `pos` som matchar valen på alla TIDIGARE axlar?
     Senare axlar ignoreras – det är hela kaskaden. `kopbar` kräver dessutom att den går att köpa. */
  finns(pos, varde, val, kopbar = false) {
    return this.variants.some((v) => {
      if (v.options[pos] !== varde) return false;
      if (kopbar && !v.available) return false;
      for (let i = 0; i < pos; i++) {
        if (val[i] !== undefined && v.options[i] !== val[i]) return false;
      }
      return true;
    });
  }

  currentVariant() {
    /* 🧨 PRODUKT UTAN VAL (bara "Default Title") – rättat 2026-10-13. Väljaren renderas tom
       (wubs-variant-options hoppar över has_only_default_variant), så selectedValues() är [] och
       jämförelsen nedan blev "Default Title" === undefined → ingen variant → setATC(null) →
       köpknappen AVSTÄNGD med texten "Välj kombination". Servern renderade knappen påslagen; det
       var skriptet som stängde av den direkt vid laddning. Uppmätt på LIVE: Fogmassa i ek, Morfars
       vedkorg och Ämne för överliggare i ek gick inte att köpa från produktsidan. Hittat när
       konfiguratorernas produktlåda testades variant för variant.
       Utan valbara axlar och med EN variant finns inget att välja – den varianten är valet. */
    if (this.variants.length === 1 && !this.querySelector('[data-option-input]')) return this.variants[0];
    const vals = this.selectedValues();
    return this.variants.find((v) => v.options.every((o, i) => o === vals[i]));
  }

  setOptions(opts) {
    this.querySelectorAll('[data-option-position]').forEach((fs) => {
      const pos = Number(fs.dataset.optionPosition) - 1;
      const inp = Array.from(fs.querySelectorAll('[data-option-input]')).find((i) => i.value === opts[pos]);
      if (inp) inp.checked = true;
    });
  }

  onSelect(changed) {
    if (changed) this.losKaskad(Number(changed.dataset.optionInput) - 1);
    let variant = this.currentVariant();
    // Nödutgång. Kaskaden ovan håller kombinationen giltig, så grenen ska inte nås – men en
    // saknad variant får aldrig frysa pris och bild, så den står kvar.
    if (!variant && changed) {
      const pos = Number(changed.dataset.optionInput) - 1;
      const fallback =
        this.variants.find((v) => v.available && v.options[pos] === changed.value) ||
        this.variants.find((v) => v.options[pos] === changed.value);
      if (fallback) { this.setOptions(fallback.options); variant = fallback; }
    }
    this.refreshAvailability();
    this.update(variant);
    this.querySelectorAll('[data-option-position]').forEach((fs) => {
      const checked = fs.querySelector('[data-option-input]:checked');
      const out = fs.querySelector('[data-opt-selected]');
      if (checked && out) out.textContent = checked.value;
    });
  }

  /* KASKAD vid byte (Liam 2026-09-11). Axlarna OVANFÖR den ändrade står kvar och den ändrade
     är kundens val. Varje axel UNDER behåller sitt värde om det fortfarande bildar en variant
     med valen ovanför – byter man Ask → Ek står "Oljad" kvar – annars tar den första värde som
     gör det, i första hand ett som går att köpa. Ett byte längre ned ändrar alltså ALDRIG ett
     val högre upp. (Förut valdes "första variant med det klickade värdet" utan hänsyn till
     valen ovanför, och den vägen kunde byta ett högre val.) */
  losKaskad(fran) {
    const val = this.valPerPosition();
    Array.from(this.querySelectorAll('[data-option-position]'))
      .map((fs) => ({ fs, pos: Number(fs.dataset.optionPosition) - 1 }))
      .filter((a) => a.pos > fran)
      .sort((a, b) => a.pos - b.pos)
      .forEach(({ fs, pos }) => {
        if (val[pos] !== undefined && this.finns(pos, val[pos], val)) return;
        const inputs = Array.from(fs.querySelectorAll('[data-option-input]'));
        const nytt =
          inputs.find((i) => this.finns(pos, i.value, val, true)) ||
          inputs.find((i) => this.finns(pos, i.value, val));
        if (!nytt) return;
        nytt.checked = true;
        val[pos] = nytt.value;
      });
  }

  /* KASKAD i visningen (Liam 2026-09-11 "dölj varianter som inte är tillgängliga i steg 2 och 3").
     Första axeln visar alltid alla värden; varje senare axel visar bara de värden som bildar en
     variant med valen OVANFÖR. Resten döljs – förut gråades de ut och ströks över. Senare axlar
     ignoreras, så ett högre val kan aldrig låsas av ett lägre och man kan alltid byta tillbaka.
     ⚠️⚠️ Samma regel står i snippets/wubs-variant-options.liquid för första renderingen – ändra
     båda eller ingen. Det valda värdet döljs aldrig: kunden ska alltid se vad som är valt. */
  refreshAvailability() {
    const val = this.valPerPosition();
    /* Måttbeställd variant → axlar som kaskaden lämnat med högst ETT synligt värde är döda
       platshållare ("Kulör: Måttbeställd", "Längd: Egna mått") och göms. Priskonfiguratorn
       frågar efter måtten själv. Materialaxeln har kvar sina tre värden och står därför kvar –
       den är enda vägen tillbaka till lagervaran.
       ⚠️⚠️ Samma regel står i snippets/wubs-variant-options.liquid för första renderingen.
       ⚠️ Radioknappen lämnas ikryssad och enabled: döljandet är kosmetiskt, och currentVariant()
          läser den vidare. */
    const aktuell = this.currentVariant();
    const matt = !!aktuell && this.mattVarianter.has(Number(aktuell.id));
    this.querySelectorAll('[data-option-position]').forEach((fs) => {
      const pos = Number(fs.dataset.optionPosition) - 1;
      let synliga = 0;
      fs.querySelectorAll('[data-option-input]').forEach((input) => {
        const dold = !input.checked && !this.finns(pos, input.value, val);
        const label = input.closest('.wubs-pdp__opt-value');
        if (label) label.hidden = dold;
        input.disabled = dold;
        if (!dold) synliga += 1;
      });
      fs.hidden = matt && synliga <= 1;
    });
  }

  update(variant, opts = {}) {
    if (!variant) {
      // ingen giltig kombination vald → lås köp
      this.setATC(null);
      return;
    }
    // pris
    const priceEl = this.pdp?.querySelector('[data-pdp-price]');
    if (priceEl) this.renderPrice(priceEl, variant);
    // bild – slå upp variantens media i kartan (variant.featured_media saknas i JSON:en).
    // Vid inladdning (skipMedia) rör vi INTE bilden – då visas produktens huvudbild som Liquid renderat.
    // Först när kunden aktivt byter variant byter vi till variantens bild.
    const mid = this.variantMedia[variant.id] || (variant.featured_media && variant.featured_media.id);
    /* Variantgalleri: bara vald variants bilder + de gemensamma (se VARIANTGALLERI i wubs-product.liquid).
       Hamnar den aktiva bilden bland de dolda byts den även vid inladdning. */
    const synliga = this.filtreraGalleri(variant);
    const aktiv = this.pdp?.querySelector('[data-media-id].is-active');
    const aktivDold = aktiv && aktiv.hasAttribute('data-media-dold');
    if (!opts.skipMedia || aktivDold) {
      if (mid && (!synliga || synliga.has(String(mid)))) this.showMedia(mid);
      else if (synliga && synliga.size) this.showMedia(synliga.values().next().value);
    }
    // sku – ALLA block. Specifikationstabellen renderas i flera dolda kopior (en per unik
    // variantspec), så ett querySelector hade bara uppdaterat den första och de andra visat
    // artikelnumret från sidladdningen.
    /* Saknar varianten artikelnummer ska raden FÖRSVINNA, inte behålla den förra variantens.
       Etikett och värde döljs ihop – annars står "Artikelnr" kvar utan svar. */
    const harSku = !!variant.sku;
    this.pdp?.querySelectorAll('[data-pdp-sku]').forEach((el) => {
      el.textContent = harSku ? variant.sku : '';
      el.hidden = !harSku;
    });
    this.pdp?.querySelectorAll('[data-pdp-sku-label]').forEach((el) => { el.hidden = !harSku; });
    /* Axelraderna i specifikationen – samma princip som artikelnumret: värdet hämtas ur
       varianten, det lagras inte. Alla block uppdateras, även de dolda. */
    this.pdp?.querySelectorAll('[data-pdp-axel]').forEach((el) => {
      const i = Number(el.dataset.pdpAxel);
      const v = variant.options && variant.options[i];
      if (v) el.textContent = v;
    });
    /* Specifikationstabellen: visa det block som hör till varianten. Kartan skrivs av
       wubs-product.liquid; saknas den finns bara ett block och ingenting ska växlas. */
    const spKarta = this.pdp?.querySelector('[data-spec-karta]');
    if (spKarta) {
      let karta = null;
      try { karta = JSON.parse(spKarta.textContent); } catch (e) { karta = null; }
      const nyckel = karta && karta[variant.id];
      /* 🧨 OKÄND VARIANT → DÖLJ ALLT, visa aldrig ett annat blocks mått.
         Kartan byggs när sidan renderas. Står sidan kvar i en flik medan varianter läggs till
         saknas de nya id:na, och utan den här grenen lämnades blocket orört medan axelraderna
         (som läser varianten direkt) uppdaterades – resultatet blev "Modell: Förlängd" ovanför
         Klassiskens 65/48/42/Ø50. Uppmätt skarpt 2026-10-01 efter att beslagen slogs ihop.
         En tom specruta säger "ladda om"; fel mått på ett väggfäste säger ingenting alls. */
      if (nyckel) {
        this.pdp.querySelectorAll('[data-spec-dl]').forEach((dl) => {
          dl.hidden = dl.dataset.specDl !== nyckel;
        });
      } else {
        this.pdp.querySelectorAll('[data-spec-dl]').forEach((dl) => { dl.hidden = true; });
      }
    }
    /* önskelista-variant. ALLA hjärtan: köprutan och prisrutorna har varsitt, och på
       sattsteg-igenbyggnad är det första i den dolda furu-rutan. Bara det uppdaterades, så
       önskelistan sparade varianten sidan laddades med (2026-11-03). */
    this.pdp?.querySelectorAll('[data-wubs-wishlist-toggle]').forEach((wl) => {
      wl.dataset.wubsWishlistVariant = variant.id;
    });
    /* Konfiguratorknappens `?tra=` följer variantvalet (2026-10-04). Serversvaret bär redan
       träslaget för den variant sidan laddades med (wubs-konfig-knapp.liquid) – men byter kunden
       träslag här utan omladdning skulle knappen annars skicka hen till konfiguratorn med det
       GAMLA träslaget förvalt. Ett förval som motsäger kundens senaste klick är sämre än inget.
       ⚠️ Axeln kommer från Liquid (`data-konfig-tra-axel`), så regeln för vilken axel som är
          träslaget finns på ETT ställe. Saknas attributet har produkten ingen träslagsaxel. */
    const traLank = this.pdp?.querySelector('a[data-konfig-tra-axel]');
    if (traLank) {
      const pos = Number(traLank.dataset.konfigTraAxel) - 1;
      const ytPos = Number(traLank.dataset.konfigYtAxel || 0) - 1;
      const tra = pos >= 0 ? variant.options[pos] : '';
      const yt = tra && ytPos >= 0 ? variant.options[ytPos] : '';
      try {
        const u = new URL(traLank.href, window.location.origin);
        if (tra) u.searchParams.set('tra', tra);
        else u.searchParams.delete('tra');
        if (yt) u.searchParams.set('yt', yt);
        else u.searchParams.delete('yt');
        traLank.href = u.toString();
      } catch (e) {}
    }
    // URL
    if (this.productUrl) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    this.setATC(variant);
    // Broadcast så fristående sektioner (t.ex. provbit-CTA) kan följa vald variant.
    // ⚠️ MEN inte från quick add-panelen. Fyra PDP-lyssnare hänger på det här eventet
    // (wubs-pdp-provbit, wubs-provbit, wubs-matt-konfig, wubs-pris-konfig) – öppnar kunden
    // quick add för en RELATERAD produkt på en produktsida skulle de byta variant på sidan
    // bakom drawern. Vakten sitter här i stället för i varje lyssnare: en plats att hålla
    // rätt, och nya lyssnare skyddas automatiskt.
    if (!this.closest('[data-wubs-quickadd]')) {
      document.dispatchEvent(new CustomEvent('wubs:variant:change', { detail: { variantId: variant.id } }));
    }
  }

  setATC(variant) {
    const btn = this.pdp?.querySelector('[data-pdp-atc]');
    const idInput = this.pdp?.querySelector('[data-pdp-variant-id]');
    const label = this.pdp?.querySelector('[data-pdp-atc-label]');
    if (!btn) return;
    // Knapptexterna kan sättas i produktsektionens inställningar (data-label-* på knappen).
    // Quick add-panelens knapp saknar attributen och får exakt samma texter som förut.
    const txt = (k, d) => btn.dataset[k] || d;
    if (!variant) {
      btn.disabled = true;
      if (label) label.textContent = txt('labelChoose', 'Välj kombination');
      return;
    }
    if (idInput) idInput.value = variant.id;
    // OBS: sätt INTE data-wubs-atc här – då kapar cart-drawerns globala klick-handler
    // submit-eventet (preventDefault) och lägger i korgen med hårdkodat antal 1.
    // PDP:ns egna submit-handler (nedan) läser kvantitetsväljaren och öppnar drawern.
    btn.disabled = !variant.available;
    if (label) label.textContent = variant.available ? txt('labelAdd', 'Lägg i varukorg') : txt('labelSoldout', 'Slutsåld');
  }

  renderPrice(el, variant) {
    // ⚠️ Avrundat till HEL KRONA – butikens money-format är {{amount_no_decimals_with_space_separator}}
    // och Liquid avrundar. Utan Math.round hade ett kampanjpris som 109,65 kr skrivits med öre här
    // medan serverns rendering av samma sida sa 110 kr. (window.Shopify.formatMoney finns inte i
    // temat; den grenen är kvar för en framtida app som definierar den.)
    const fmt = (c) => window.Shopify?.formatMoney
      ? window.Shopify.formatMoney(c, window.Shopify.money_format || '{{amount}} kr')
      : Math.round(c / 100).toLocaleString('sv-SE') + ' kr';
    // "Från" kan sättas i inställningarna (data-from-label). Texten hamnar i innerHTML → escapas.
    const fromTxt = (el.dataset.fromLabel || 'Från').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const fromLbl = el.dataset.from ? `<span class="wubs-pdp__from-label">${fromTxt}</span> ` : '';

    // KAMPANJPRIS – procenten sätts i Liquid (data-kampanj-pct) ur snippets/wubs-kampanj.liquid.
    // Samma uträkning som snippets/wubs-price.liquid: heltalsdivision i öre, sedan avrundning till
    // krona i formateringen. Ändra båda eller ingen, annars byter priset värde vid första variantbytet.
    const pct = Number(el.dataset.kampanjPct) || 0;
    const jamfor = this.jamforpris(el, variant, pct, fmt);
    if (pct > 0 && pct < 100) {
      const kampanj = Math.floor(variant.price * (100 - pct) / 100);
      el.innerHTML = fromLbl +
        `<span class="wubs-price wubs-price--sale wubs-price--kampanj">` +
        `<span class="wubs-price__amount">${fmt(kampanj)}</span>` +
        `<s class="wubs-price__compare">${fmt(variant.price)}</s>` +
        `</span>` + jamfor;
      return;
    }

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    el.innerHTML = fromLbl +
      `<span class="wubs-price${onSale ? ' wubs-price--sale' : ''}">` +
      `<span class="wubs-price__amount">${fmt(variant.price)}</span>` +
      (onSale ? `<s class="wubs-price__compare">${fmt(variant.compare_at_price)}</s>` : '') +
      `</span>` + jamfor;
  }

  // JÄMFÖRPRIS ("Jämförpris 252 kr/m") ur variantens enhetspris – samma regler som
  // snippets/wubs-jamforpris.liquid: kampanjprocenten räknas i öre med heltalsdivision, 'm2' → 'm²',
  // 'item' → 'st'. Ändra båda eller ingen. Tom sträng när varianten saknar enhetspris.
  jamforpris(el, variant, pct, fmt) {
    const m = variant.unit_price_measurement;
    if (!m || variant.unit_price == null) return '';
    const pris = pct > 0 && pct < 100 ? Math.floor(variant.unit_price * (100 - pct) / 100) : variant.unit_price;
    let enhet = { m2: 'm²', item: 'st' }[m.reference_unit] || m.reference_unit;
    if (Number(m.reference_value) !== 1) enhet = `${m.reference_value} ${enhet}`;
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const etikett = el.dataset.jamforLabel ? `${esc(el.dataset.jamforLabel)} ` : '';
    return `<span class="wubs-jamforpris wubs-pdp__jamforpris">${etikett}${fmt(pris)}/${esc(enhet)}</span>`;
  }

  /* Alt "Träslag|Kulör" (flera nycklar med ;) = bilden hör till varianter med alla de värdena;
     alt utan "|" = gemensam. Samma regel som Liquid kör vid inladdning. Returnerar de synliga
     media-id:na (null = inget filtreras: produkten saknar nycklade bilder eller varianten egna). */
  filtreraGalleri(variant) {
    if (!this.pdp) return null;
    const slides = Array.from(this.pdp.querySelectorAll('[data-media-id]'));
    // tomma delar räknas inte – "Förnicklad|" är en nyckel för en enda axel. Varje värde räcker till
    // en del: "Aluminium|Aluminium" gäller inte "Aluminium · Borstat stål" (docs/prisadmin-appen.md).
    const matchar = (nyckel) => nyckel.split(';').some((k) => {
      const kvar = [...variant.options];
      return k.split('|').map((d) => d.trim()).filter(Boolean).every((d) => {
        const i = kvar.indexOf(d);
        if (i < 0) return false;
        kvar.splice(i, 1);
        return true;
      });
    });
    const nycklade = slides.filter((s) => s.dataset.mediaKey);
    const filtrera = nycklade.length > 0 && nycklade.some((s) => matchar(s.dataset.mediaKey));
    const synliga = new Set();
    slides.forEach((s) => {
      const vis = !filtrera || !s.dataset.mediaKey || matchar(s.dataset.mediaKey);
      s.toggleAttribute('data-media-dold', !vis);
      if (vis) synliga.add(s.dataset.mediaId);
    });
    const thumbs = this.pdp.querySelector('[data-pdp-thumbs]');
    if (thumbs) {
      thumbs.querySelectorAll('[data-pdp-thumb]').forEach((t) => {
        const li = t.closest('li') || t;
        li.toggleAttribute('data-media-dold', !synliga.has(t.dataset.pdpThumb));
      });
      thumbs.classList.toggle('wubs-pdp__thumbs--en', synliga.size <= 1);
      // miniatyrernas storlek räknas på antalet synliga (syncThumbs lyssnar på resize)
      window.dispatchEvent(new Event('resize'));
    }
    return filtrera ? synliga : null;
  }

  showMedia(mediaId) {
    wubsVisaMedia(this.pdp, mediaId);
  }
  paintSwatches() {
    // för varje swatch-plats: hitta första variant vars options innehåller värdet + dess bild.
    // Saknas bild (t.ex. Shopify deduplicerade delad källbild) → visa namnet som textchip.
    this.querySelectorAll('[data-swatch-for]').forEach((sw) => {
      const val = sw.dataset.swatchFor;
      // Värdet på SIN axel: beslagets "Aluminium" finns både som modell och som ytbehandling.
      const axel = Number(sw.closest('[data-option-position]').dataset.optionPosition) - 1;
      const v = this.variants.find((vv) => vv.options[axel] === val && vv.featured_image);
      const src = v && v.featured_image && (v.featured_image.src || v.featured_image);
      const label = sw.closest('.wubs-pdp__opt-value');
      if (src) sw.style.backgroundImage = `url(${src})`;
      else if (label) label.classList.add('is-noimg');
    });
  }
}
customElements.define('wubs-variant-picker', WubsVariantPicker);

/* Bildbytet låg som metod på variantväljaren. Produkter med PRISMODELL renderar ingen
   variantväljare (varianterna är Apippas slaskvarianter), så där fanns ingen väg in i
   galleriet alls. Funktionen ligger därför på modulnivå och tar galleriet som argument.
   Metoden finns kvar och pekar hit – inget anrop behövde skrivas om. */
function wubsVisaMedia(pdp, mediaId) {
  if (!pdp) return;
  pdp.querySelectorAll('[data-media-id]').forEach((s) => {
    const active = Number(s.dataset.mediaId) === Number(mediaId);
    s.classList.toggle('is-active', active);
    // Lazy-bilder i dolda slides laddas aldrig – tvinga fram den synliga variantbilden
    if (active) {
      const img = s.querySelector('img[loading="lazy"]');
      if (img && !img.complete) img.loading = 'eager';
    }
  });
  pdp.querySelectorAll('[data-pdp-thumb]').forEach((t) =>
    t.classList.toggle('is-active', Number(t.dataset.pdpThumb) === Number(mediaId)));
  wubsCentreraThumb(pdp);
}

/* ── BILD PER PRISVAL (Liam 2026-10-05) ─────────────────────────────────────────────
   Prisrutans fält byter aldrig variant, så galleriet kan inte följa med på vanlig
   variantväg. Prisrutan ropar ut valet (`wubs:prisbild`) och kopplingen till bilden är
   dess ALT-TEXT, som Tom redan styr i admin:

       Avrundningar: Radie 25      ← fältets namn, kolon, valets namn, som i prisrutan

   Ingen ny inställning och ingen lista i temat som kan hamna i otakt med prismodellen.
   Slug-formen (`avrundningar=radie_25`) duger också och överlever omdöpta etiketter.

   ⚠️ Jämförelsen normaliserar bort skiftläge, mellanslag och skiljetecken. Prismodellerna
      stavar inte som variantaxlarna – "16mm MDF" mot "16 mm MDF" – och en nyckel som kräver
      exakt stavning hade blivit tyst fel.
   ⚠️ FINNS INGEN BILD GÖRS INGENTING. Ett val utan bild ska lämna galleriet i fred; att tömma
      det eller hoppa till första bilden vore sämre än att stå still.
   ⚠️ Galleriet FILTRERAS inte (till skillnad från variantgalleriet). Bilderna på rak ände och
      radie hör till samma produkt och ska gå att bläddra till i lugn och ro. */
(function () {
  const norm = (t) => (t || '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  document.addEventListener('wubs:prisbild', function (e) {
    const d = e.detail || {};
    const pdp = (e.target.closest && e.target.closest('[data-wubs-pdp]'))
      || document.querySelector('[data-wubs-pdp]');
    if (!pdp || !d.etikett || !d.varde) return;
    const nycklar = [norm(d.etikett + d.varde), norm(d.nyckel + d.slug)];
    const traff = Array.from(pdp.querySelectorAll('[data-media-alt]'))
      .find((s) => nycklar.indexOf(norm(s.dataset.mediaAlt)) !== -1);
    if (traff) wubsVisaMedia(pdp, traff.dataset.mediaId);
  });
})();

/* Håller den aktiva miniatyren i mitten av miniatyr-scrollen.
   Utan det kan man klicka sig nedåt tills den aktiva bilden hamnar utanför synfältet –
   man ser vilken bild som visas i stora rutan men inte var i serien man befinner sig.
   scrollIntoView({block:'center'}) duger inte: den scrollar även SIDAN när miniatyren
   ligger nära viewportkanten. Räkna i stället fram scrollpositionen inuti listan.
   TVÅ RIKTNINGAR (2026-09-11): desktop är en lodrät kolumn (scrollTop), mobil en vågrät rad
   (scrollLeft). Riktningen läses ur listans flex-direction – det är stilmallen som äger
   layouten, och en egen brytpunkt här hade kunnat hamna i otakt med den.
   ⚠️ Mobilraden har scroll-snap: webbläsaren flyttar målet till närmaste miniatyrkant, så den
   aktiva hamnar i mitten ± en halv miniatyr. Den är ändå alltid helt i vy. */
function wubsCentreraThumb(pdp, direkt) {
  const vp = pdp && pdp.querySelector('[data-pdp-thumbvp]');
  const aktiv = vp && vp.querySelector('[data-pdp-thumb].is-active');
  if (!vp || !aktiv) return;
  const vagrat = getComputedStyle(vp).flexDirection.startsWith('row');
  const max = vagrat ? vp.scrollWidth - vp.clientWidth : vp.scrollHeight - vp.clientHeight;
  if (max < 2) return; // inget att scrolla
  const rad = aktiv.closest('li') || aktiv;
  // ⚠️ offsetTop mäts mot närmaste POSITIONERADE förälder, inte mot scroll-listan – är
  // listan opositionerad pekar värdet någon annanstans och inget scrollas. Rects är
  // oberoende av offsetParent och funkar oavsett hur listan är stilad.
  const r = rad.getBoundingClientRect();
  const v = vp.getBoundingClientRect();
  const mal = vagrat
    ? vp.scrollLeft + (r.left - v.left) - (v.width - r.width) / 2
    : vp.scrollTop + (r.top - v.top) - (v.height - r.height) / 2;
  vp.scrollTo({
    [vagrat ? 'left' : 'top']: Math.max(0, Math.min(mal, max)),
    behavior: direkt ? 'auto' : 'smooth',
  });
}

/* Galleri: klick på miniatyr byter huvudbild */
document.addEventListener('click', (e) => {
  const thumb = e.target.closest('[data-pdp-thumb]');
  if (!thumb) return;
  const pdp = thumb.closest('[data-wubs-pdp]');
  const id = thumb.dataset.pdpThumb;
  pdp.querySelectorAll('[data-media-id]').forEach((s) =>
    s.classList.toggle('is-active', s.dataset.mediaId === id));
  pdp.querySelectorAll('[data-pdp-thumb]').forEach((t) =>
    t.classList.toggle('is-active', t === thumb));
  wubsCentreraThumb(pdp);
});

/* Produktformulär: lägg i drawern i stället för sidladdning (no-JS → native submit) */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('[data-pdp-form]');
  if (!form) return;
  const drawer = document.querySelector('wubs-cart-drawer');
  if (!drawer) return; // ingen drawer → låt native add ske
  e.preventDefault();
  const id = form.querySelector('[data-pdp-variant-id]')?.value;
  const btn = form.querySelector('[data-pdp-atc]');
  if (!id) return;
  const qty = parseInt(form.querySelector('[data-pdp-qty-input]')?.value, 10) || 1;
  /* Kapad höjd: fältet fyller ett dolt `properties[Höjd]`, tomt när höjden är materialets egen.
     Formulärets properties postas inte av sig själva här – submit går via fetch.
     ⚠️ Klampa om FÖRE läsningen. Normalt hinner blurrens `change` före klicket på köpknappen,
        men den ordningen är webbläsarens att bestämma – och ett okontrollerat tal går rakt in i
        ordern. Klampningen är idempotent, så det kostar inget att göra den en gång till. */
  const hojdFalt = form.querySelector('[data-pdp-hojd]');
  if (hojdFalt) { wubsHojdKlampa(hojdFalt); wubsHojdSynka(form); }
  const hojdProp = form.querySelector('[data-pdp-hojd-prop]');
  const props = hojdProp?.value ? { [hojdProp.name.replace(/^properties\[|\]$/g, '')]: hojdProp.value } : null;
  const stickyAtc = form.closest('[data-wubs-pdp]')?.querySelector('[data-pdp-sticky-atc]');
  drawer.rensaKopFel(btn);
  drawer.rensaKopFel(stickyAtc);
  try {
    await drawer.addVariant(id, Math.max(1, qty), btn, props);
  } catch (err) {
    /* Korgen sa nej eller nätet är borta (2026-11-03): beskedet vid knappen, och ingen låda utan varan.
       Köpte kunden från den fasta raden är huvudknappen utanför vyn, så då står det där också. */
    drawer.visaKopFel(btn);
    if (stickyAtc?.closest('[data-pdp-sticky].is-visible')) drawer.visaKopFel(stickyAtc);
    return;
  }
  form.dispatchEvent(new CustomEvent('wubs:kop:klar', { bubbles: true })); // stänger quick add
  drawer.open();
});

/* ---------- Kapad höjd (2026-10-06) ----------
   Produkter vars variant bär `custom.material_hojd` får ett höjdfält i köprutan. Taket är
   materialets egen höjd, golvet 120 mm. Priset påverkas inte – skivan är densamma – så det här
   är enbart ett mått som följer med raden.
   ⚠️ Klampningen sitter på `change`, inte `input`: klampar man medan kunden skriver hoppar "1"
      direkt till 120 och det går inte att skriva 185. (Samma fälla som i wubs-matt-konfig.js –
      ett test som bara skickar `input` ser däremot ut som att gränsen saknas.)
   ⚠️ Egenskapen sätts BARA när höjden avviker från taket, se kommentaren i wubs-product.liquid. */
/* Deklarationer (inte uttryck) – de hissas, så submit-hanteraren ovanför kan kalla på dem. */
function wubsHojdFalten(root) {
  return {
    falt: root.querySelector('[data-pdp-hojd]'),
    prop: root.querySelector('[data-pdp-hojd-prop]'),
    grans: root.querySelector('[data-pdp-hojd-grans]'),
    wrap: root.querySelector('[data-pdp-hojd-falt]'),
  };
}

function wubsHojdSynka(form) {
  const { falt, prop } = wubsHojdFalten(form);
  if (!falt || !prop) return;
  const max = parseInt(falt.max, 10) || 0;
  const v = parseInt(falt.value, 10);
  /* 🔴 "(kapad)" ÄR INTE PYNT. Följesedeln hoppar över en egenskap vars VÄRDE redan står i
     radens namn (`fs_namn contains fs_v`, dubblettskydd för t.ex. Träslag som finns både som
     variantaxel och prisregel) – och den jämförelsen är ren substrängmatchning. Höjden 200 mm
     på varianten "… / 2200 mm" hade därför tyst fallit bort, och verkstaden hade sågat 220.
     Med suffixet kan värdet aldrig vara en delsträng av varianttiteln. Det säger dessutom det
     verkstaden behöver veta: den här ska kapas. */
  prop.value = Number.isFinite(v) && v > 0 && v !== max ? `${v} mm (kapad)` : '';
}

function wubsHojdKlampa(falt) {
  const min = parseInt(falt.min, 10) || 0;
  const max = parseInt(falt.max, 10) || 0;
  let v = parseInt(falt.value, 10);
  if (!Number.isFinite(v) || v > max) v = max;
  if (v < min) v = min;
  falt.value = v;
}

(function () {
  document.addEventListener('input', (e) => {
    const falt = e.target.closest('[data-pdp-hojd]');
    if (falt) wubsHojdSynka(falt.closest('[data-pdp-form]'));
  });

  document.addEventListener('change', (e) => {
    const falt = e.target.closest('[data-pdp-hojd]');
    if (!falt) return;
    wubsHojdKlampa(falt);
    wubsHojdSynka(falt.closest('[data-pdp-form]'));
  });

  /* Byter kunden material byter taket. Saknar den nya varianten materialhöjd finns ingen kapning
     att erbjuda – då göms fältet och egenskapen nollas, annars hade ett gammalt mått följt med. */
  document.addEventListener('wubs:variant:change', (e) => {
    document.querySelectorAll('[data-pdp-hojd-karta]').forEach((skript) => {
      const form = skript.closest('[data-pdp-form]');
      if (!form) return;
      /* Bara formuläret som FAKTISKT bytte variant. setATC() har redan skrivit det nya id:t i det
         dolda fältet när eventet går ut, så jämförelsen pekar ut rätt köpruta – utan den hade en
         quick add-panel på samma sida fått ett variant-id som inte finns i dess karta och gömt
         sitt eget höjdfält. */
      if (form.querySelector('[data-pdp-variant-id]')?.value !== String(e.detail?.variantId)) return;
      const { falt, prop, grans, wrap } = wubsHojdFalten(form);
      if (!falt || !prop || !wrap) return;
      let karta = {};
      try { karta = JSON.parse(skript.textContent) || {}; } catch (err) { return; }
      const max = Number(karta[e.detail?.variantId]) || 0;
      const min = parseInt(falt.min, 10) || 0;
      if (max <= min) {
        wrap.hidden = true;
        prop.value = '';
        return;
      }
      const gammalt = parseInt(falt.max, 10) || 0;
      const v = parseInt(falt.value, 10);
      wrap.hidden = false;
      falt.max = max;
      if (grans) grans.textContent = `${min}–${max}`;
      /* Stod fältet på förra materialets fulla höjd är det ett förval, inte ett kundval –
         följ med upp. Ett eget mått behålls, men klipps till det nya taket. */
      if (!Number.isFinite(v) || v === gammalt || v > max) falt.value = max;
      wubsHojdSynka(form);
    });
  });
})();

/* Kvantitetsstegare (− / fält / +). Klampar till minst 1; sanerar fritext. */
(function () {
  function clampQty(input) {
    let v = parseInt(input.value, 10);
    if (!Number.isFinite(v) || v < 1) v = 1;
    input.value = v;
  }
  document.addEventListener('click', (e) => {
    const dec = e.target.closest('[data-pdp-qty-dec]');
    const inc = e.target.closest('[data-pdp-qty-inc]');
    if (!dec && !inc) return;
    const wrap = (dec || inc).closest('[data-pdp-qty]');
    const input = wrap?.querySelector('[data-pdp-qty-input]');
    if (!input) return;
    let v = parseInt(input.value, 10);
    if (!Number.isFinite(v)) v = 1;
    v = inc ? v + 1 : v - 1;
    input.value = Math.max(1, v);
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-pdp-qty-input]');
    if (input) clampQty(input);
  });
})();

/* Galleri-höjd + sticky på KORTASTE kolumnen (desktop). Liam 2026-08-03.
   - Thumbs kapas till huvudbildens höjd → media förblir 1:1 och kort (scrollar vid många).
   - Den kortaste av galleri/info fastnar (sticky) medan den längre scrollar. */
(function () {
  const mq = window.matchMedia('(min-width: 990px)');

  const eachPdp = (fn) => document.querySelectorAll('[data-wubs-pdp]').forEach(fn);

  // Upp/ner-pilar (som Suntribe): visa dem bara när viewporten kan scrollas
  function updateArrows(wrap, vp) {
    if (!wrap || !vp) return;
    const scrollable = vp.scrollHeight - vp.clientHeight > 2;
    wrap.classList.toggle('has-prev', scrollable && vp.scrollTop > 2);
    wrap.classList.toggle('has-next', scrollable && vp.scrollTop < vp.scrollHeight - vp.clientHeight - 2);
  }

  function syncThumbs(pdp) {
    const stage = pdp.querySelector('.wubs-pdp__stage');
    const media = pdp.querySelector('.wubs-pdp__media');
    const wrap = pdp.querySelector('[data-pdp-thumbs]');
    const vp = pdp.querySelector('[data-pdp-thumbvp]');
    if (!stage || !media || !vp) return;
    // Bara synliga miniatyrer räknas – variantgalleriet döljer de andra kulörernas bilder.
    const thumbs = Array.from(vp.querySelectorAll('[data-pdp-thumb]'))
      .filter((t) => !(t.closest('li') || t).hasAttribute('data-media-dold'));

    // En enda synlig bild: raden är dold – stagen ska inte lämna plats åt den (som utan miniatyrer).
    if (!mq.matches || thumbs.length <= 1) {
      // mobil: rensa desktop-inline så CSS-radlayouten (thumbs på rad) gäller
      thumbs.forEach((t) => { t.style.width = ''; t.style.height = ''; });
      stage.style.width = '';
      wrap.style.width = '';
      vp.style.maxHeight = '';
      vp.style.overflowY = '';
      vp.removeAttribute('data-lenis-prevent'); // mobilraden scrollar i sidled – lodrätt hjul är sidans
      return;
    }

    // Desktop: huvudbild + thumbnails ska fylla HELA galleri-kolumnen (60 %) utan luft.
    //   HGAP = mellanrum bild↔thumbs, VGAP = mellanrum mellan thumbs.
    //   "Ideal" bildstorlek = den där thumbs (bild/antal) fyller både höjd och bredd samtidigt.
    //   Bilden kapas sedan på 80vh; kvarvarande bredd går till thumbs (de växer), som då scrollar.
    const mediaW = media.clientWidth;
    const n = thumbs.length || 1;
    const HGAP = 14;  // 1.4rem – bild↔thumbs
    const VGAP = 10;  // 1rem – mellan thumbs
    // ⚠️ "ideal" optimerar bort ALL luft, men delar restbredden på antalet miniatyrer –
    //    med 13 bilder (plansteg) blev de 48 px och såg suddiga/obrukbara ut. Miniatyren
    //    måste ha en egen storleksram; huvudbilden får det som blir över. Strippen scrollar
    //    redan, så fler bilder ska göra listan längre – inte rutorna mindre.
    const MIN_T = 96;   // under detta går motivet inte att urskilja
    const MAX_T = 190;  // över detta börjar den stjäla från huvudbilden
    const ideal = (n * mediaW - n * HGAP + (n - 1) * VGAP) / (n + 1);
    let thumbW = mediaW - Math.min(0.8 * window.innerHeight, ideal) - HGAP;
    thumbW = Math.min(Math.max(thumbW, MIN_T), MAX_T);
    let img = Math.min(0.8 * window.innerHeight, mediaW - thumbW - HGAP);
    img = Math.max(img, 0);

    stage.style.width = Math.round(img) + 'px';
    thumbs.forEach((t) => { t.style.width = Math.round(thumbW) + 'px'; t.style.height = Math.round(thumbW) + 'px'; });
    wrap.style.width = Math.round(thumbW) + 'px';
    vp.style.maxHeight = Math.round(img) + 'px';
    const totalH = n * thumbW + (n - 1) * VGAP;
    vp.style.overflowY = totalH > img + 1 ? 'auto' : 'visible';
    updateArrows(wrap, vp);
    /* HJULET ÖVER MINIATYRERNA SKA SCROLLA LISTAN (Tom/Liam 2026-10-26: "om man har musen över
       thumbnailsen, och skrollar, så ska det skrolla i den listan"). Listan är scrollbar, men
       sajten kör LENIS (wubs-lenis.js), som tar varje hjul på window och scrollar SIDAN – listan
       rörde sig aldrig. Lenis släpper händelser vars väg har `data-lenis-prevent` (samma som
       menyn och lådorna). Sätts bara när listan FAKTISKT har något att scrolla – mätt, inte
       uträknat: `totalH` ovan antar li = miniatyr, men li är 10 px högre (se "KVAR" i
       docs/produktsida.md), så en lista kan scrolla några tiotal px fast totalH säger nej.
       Utan något att scrolla ska hjulet vara sidans, mjukt som överallt annars. */
    if (vp.scrollHeight - vp.clientHeight > 2) vp.setAttribute('data-lenis-prevent', '');
    else vp.removeAttribute('data-lenis-prevent');
  }

  function updateSticky(pdp) {
    const media = pdp.querySelector('.wubs-pdp__media');
    const info = pdp.querySelector('.wubs-pdp__info');
    const infoSticky = pdp.querySelector('.wubs-pdp__info-sticky');
    if (!media || !info || !infoSticky) return;
    media.classList.remove('is-sticky');
    infoSticky.classList.remove('is-sticky');
    // mobil, eller sticky_info avslaget (is-static) → ingen fastnar
    if (!mq.matches || infoSticky.classList.contains('is-static')) return;
    // naturliga höjder (utan sticky) → kortaste kolumnen fastnar
    const mediaH = media.getBoundingClientRect().height;
    const infoH = info.getBoundingClientRect().height;
    if (mediaH <= infoH) media.classList.add('is-sticky');
    else infoSticky.classList.add('is-sticky');
  }

  function refresh() {
    eachPdp((pdp) => { syncThumbs(pdp); updateSticky(pdp); });
  }

  // Pil-klick → scrolla viewporten en dryg sida
  document.addEventListener('click', (e) => {
    const nav = e.target.closest('[data-pdp-thumbnav]');
    if (!nav) return;
    const wrap = nav.closest('[data-pdp-thumbs]');
    const vp = wrap && wrap.querySelector('[data-pdp-thumbvp]');
    if (!vp) return;
    const dir = nav.dataset.pdpThumbnav === 'prev' ? -1 : 1;
    vp.scrollBy({ top: dir * vp.clientHeight * 0.8, behavior: 'smooth' });
  });

  // Scroll i viewporten → uppdatera pilarna (scroll bubblar inte → capture)
  document.addEventListener('scroll', (e) => {
    const vp = e.target.closest && e.target.closest('[data-pdp-thumbvp]');
    if (vp) updateArrows(vp.closest('[data-pdp-thumbs]'), vp);
  }, true);

  let t;
  const onResize = () => { clearTimeout(t); t = setTimeout(refresh, 120); };

  // Byter galleriet riktning (desktopkolumn ↔ mobilrad, t.ex. en surfplatta som vänds) står
  // den nya scrollen på noll och den aktiva miniatyren kan hamna utanför – centrera den då.
  // ⚠️ INTE vid varje resize: mobilens adressfält ger ett resize-event varje gång det glider
  // undan, och raden hade ryckts tillbaka mitt i att kunden bläddrade i den.
  const bytRiktning = () => {
    refresh();
    eachPdp((pdp) => wubsCentreraThumb(pdp));
  };

  document.addEventListener('DOMContentLoaded', refresh);
  window.addEventListener('load', refresh);
  window.addEventListener('resize', onResize);
  if (mq.addEventListener) mq.addEventListener('change', bytRiktning);
})();

/* Accordion: mjuk höjd-animation vid öppning/stängning. Native <details> togglar direkt utan
   övergång, så vi tar över click:en på summary och animerar body-höjden 0↔auto. Liam 2026-08-04. */
(function () {
  const DUR = 300;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  // öppning bromsar in (mjuk landning); stängning accelererar ut (snappar klart – ingen krypande
  // sista pixel som tappar frames och läser som hack).
  const EASE_OPEN = 'cubic-bezier(0, 0, 0.2, 1)';
  const EASE_CLOSE = 'cubic-bezier(0.4, 0, 1, 1)';

  function animate(panel, body, open) {
    panel.dataset.animating = '1';
    body.style.overflow = 'hidden';
    if (open) {
      panel.setAttribute('open', '');
      const h = body.scrollHeight;
      body.style.height = '0px';
      body.style.opacity = '0';
      void body.offsetWidth; // tvinga reflow (preview throttlar rAF)
      // innehållet tonas in en aning EFTER att höjden börjat → mjuk start
      body.style.transition = 'height ' + DUR + 'ms ' + EASE_OPEN + ', opacity ' + DUR + 'ms ease ' + Math.round(DUR * 0.15) + 'ms';
      body.style.height = h + 'px';
      body.style.opacity = '1';
    } else {
      body.style.height = body.scrollHeight + 'px';
      body.style.opacity = '1';
      void body.offsetWidth;
      // innehållet tonas ut FÖRE höjden når 0 → sista biten kollapsar tom (ingen snap)
      body.style.transition = 'height ' + DUR + 'ms ' + EASE_CLOSE + ', opacity ' + Math.round(DUR * 0.55) + 'ms ease';
      body.style.height = '0px';
      body.style.opacity = '0';
    }
    let done = false;
    // vänta på HÖJD-transitionen specifikt (opacity slutar tidigare och skulle annars städa för fort)
    const onEnd = (e) => { if (e.propertyName === 'height') finish(); };
    const finish = () => {
      if (done) return;
      done = true;
      if (!open) panel.removeAttribute('open');
      body.style.transition = '';
      body.style.height = '';
      body.style.opacity = '';
      body.style.overflow = '';
      delete panel.dataset.animating;
      body.removeEventListener('transitionend', onEnd);
    };
    body.addEventListener('transitionend', onEnd);
    setTimeout(finish, DUR + 80); // failsafe om transitionend uteblir
  }

  document.addEventListener('click', (e) => {
    const head = e.target.closest('.wubs-pdp__panel-head');
    if (!head) return;
    const panel = head.closest('.wubs-pdp__panel');
    const body = panel && panel.querySelector('.wubs-pdp__panel-body');
    if (!panel || !body) return;
    if (reduce.matches) return;       // reducerad rörelse → låt native toggla direkt
    e.preventDefault();               // vi styr open/close själva
    if (panel.dataset.animating) return;
    animate(panel, body, !panel.hasAttribute('open'));
  });
})();

/* Sticky ATC: dyker upp när huvudköpknappen scrollats ur vy (uppåt). Speglar pris + knappstatus
   från infokolumnen och återanvänder köp-formulärets logik (add → cart-drawer). Liam 2026-08-04. */
(function () {
  document.querySelectorAll('[data-wubs-pdp]').forEach(function (pdp) {
    const sticky = pdp.querySelector('[data-pdp-sticky]');
    const atc = pdp.querySelector('[data-pdp-atc]');
    const form = pdp.querySelector('[data-pdp-form]');
    if (!sticky || !atc) return;

    const price = pdp.querySelector('[data-pdp-price]');
    const stickyPrice = sticky.querySelector('[data-pdp-sticky-price]');
    const stickyAtc = sticky.querySelector('[data-pdp-sticky-atc]');
    const stickyLabel = sticky.querySelector('[data-pdp-sticky-label]');
    const mainLabel = atc.querySelector('[data-pdp-atc-label]');

    // Synlig när köpknappen är ovanför vyn (man har scrollat förbi den). Inte när den ännu är
    // under vyn (top > 0) – då har man inte nått köpboxen än.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          const past = !e.isIntersecting && e.boundingClientRect.top < 0;
          sticky.classList.toggle('is-visible', past);
          sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
        });
      }, { threshold: 0 });
      io.observe(atc);
    }

    // Sticky-knappen kör huvudformulärets köp → samma add-to-cart + drawer som riktiga knappen.
    if (stickyAtc) {
      stickyAtc.addEventListener('click', function () {
        if (atc.disabled) return;
        atc.click();
      });
    }

    // Spegla pris + knappstatus (variantväljaren uppdaterar infokolumnens element).
    function sync() {
      if (stickyPrice && price) stickyPrice.innerHTML = price.innerHTML;
      if (stickyAtc) stickyAtc.disabled = atc.disabled;
      if (stickyLabel && mainLabel) stickyLabel.textContent = mainLabel.textContent;
    }
    sync();
    if ('MutationObserver' in window) {
      const mo = new MutationObserver(sync);
      if (price) mo.observe(price, { childList: true, subtree: true, characterData: true });
      mo.observe(atc, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
    }
  });
})();
